import { Box, Stack, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";

import AdminAiIcon from "./admin-ai-icon.component";
import AdminBootstrapIcon from "./admin-bootstrap-icon.component";
import AdminTagAliasIcon from "./admin-tag-alias-icon.component";
type AdminView = "llm-usage" | "canonical-bootstrap" | "tag-alias-bootstrap";

export const ADMIN_SIDEBAR_DESKTOP_WIDTH = 216;

interface IAdminNavItem {
  icon: FC<{ active?: boolean; size?: number }>;
  shortDescription: string;
  to: string;
  view: AdminView;
  label: string;
}

const ADMIN_NAV_ITEMS: readonly IAdminNavItem[] = [
  {
    view: "llm-usage",
    to: "/admin",
    label: "LLM Usage",
    shortDescription: "Model telemetry",
    icon: AdminAiIcon,
  },
  {
    view: "canonical-bootstrap",
    to: "/admin/canonical-bootstrap",
    label: "Canonical Bootstrap",
    shortDescription: "Run and publish",
    icon: AdminBootstrapIcon,
  },
  {
    view: "tag-alias-bootstrap",
    to: "/admin/tag-alias-bootstrap",
    label: "Tag Alias Bootstrap",
    shortDescription: "Alias review",
    icon: AdminTagAliasIcon,
  },
];

const buildSidebarShellStyles = (theme: Theme, isDesktop: boolean) => ({
  width: isDesktop ? ADMIN_SIDEBAR_DESKTOP_WIDTH : "100%",
  flexShrink: 0,
  height: isDesktop ? "100%" : "auto",
  minHeight: 0,
  borderRadius: 0,
  borderRight: isDesktop ? "1px solid" : "none",
  borderBottom: isDesktop ? "none" : "1px solid",
  borderColor: alpha(theme.palette.divider, 0.72),
  backgroundColor: "transparent",
  alignSelf: isDesktop ? "stretch" : "auto",
  position: isDesktop ? "sticky" : "static",
  top: 0,
  overflowY: isDesktop ? "auto" : "visible",
});

const buildDesktopNavItemStyles = (theme: Theme, active: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  minHeight: 52,
  width: "100%",
  px: 1.5,
  py: 0.75,
  borderRadius: 0,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, 0.12)
    : "transparent",
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  textDecoration: "none",
  transition: theme.transitions.create(["background-color", "color"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, 0.16)
      : alpha(theme.palette.action.hover, 0.72),
    color: theme.palette.text.primary,
  },
});

const buildMobileNavItemStyles = (theme: Theme, active: boolean) => ({
  minWidth: 0,
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: 0.9,
  px: 1.25,
  py: 0.9,
  borderRadius: 0,
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  textDecoration: "none",
  transition: theme.transitions.create(["background-color", "color"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.72),
    color: theme.palette.text.primary,
  },
});

const buildMobileIconFrameStyles = (theme: Theme, active: boolean) => ({
  width: 28,
  height: 28,
  borderRadius: 0,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  color: active
    ? theme.palette.primary.main
    : alpha(theme.palette.text.primary, 0.8),
});

const AdminSidebar: FC<{ activeView: AdminView }> = ({ activeView }) => (
  <>
    <Box
      sx={(theme) => ({
        display: { xs: "block", lg: "none" },
        ...buildSidebarShellStyles(theme, false),
      })}
    >
      <Stack direction="row" spacing={0} sx={{ overflowX: "auto" }}>
        {ADMIN_NAV_ITEMS.map((item) => {
          const active = item.view === activeView;
          const Icon = item.icon;

          return (
            <Box
              key={item.view}
              component={RouterLink}
              to={item.to}
              sx={(theme) => buildMobileNavItemStyles(theme, active)}
            >
              <Box
                component="span"
                sx={(theme) => buildMobileIconFrameStyles(theme, active)}
              >
                <Icon active={active} size={18} />
              </Box>
              <Stack spacing={0} sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={active ? 700 : 600}>
                  {item.label}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>

    <Box
      sx={(theme) => ({
        display: { xs: "none", lg: "block" },
        ...buildSidebarShellStyles(theme, true),
      })}
    >
      <Stack spacing={0}>
        {ADMIN_NAV_ITEMS.map((item) => {
          const active = item.view === activeView;
          const Icon = item.icon;

          return (
            <Box
              key={item.view}
              component={RouterLink}
              to={item.to}
              aria-current={active ? "page" : undefined}
              sx={(theme) => buildDesktopNavItemStyles(theme, active)}
            >
              <Box
                component="span"
                sx={(theme) => buildMobileIconFrameStyles(theme, active)}
              >
                <Icon active={active} size={18} />
              </Box>
              <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={active ? 700 : 600}
                  sx={{ color: "inherit", lineHeight: 1.2 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: active ? "text.secondary" : "inherit",
                    opacity: active ? 0.92 : 0.8,
                    lineHeight: 1.2,
                  }}
                >
                  {item.shortDescription}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  </>
);

export default AdminSidebar;
